import { useQuery } from '@tanstack/react-query';
import { features } from '@ui/components/Features';
import type { Token, TokenIconProps } from '@ui/components/TokenIcon';
import { TokenIcon } from '@ui/components/TokenIcon';
import type { TokenProtocol } from '@ui/components/useYourTokenDrawer';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import useGardenApy from '@ui/hooks/useGardenApy';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import {
  useTectonicApy,
  useTectonicSupplyUsd,
} from '@ui/hooks/usePartnerStats';
import { useSingleFinanceLcroFarmInfo } from '@ui/hooks/useSingleFinanceLcroFarmInfo';
import { useSingleFinanceLendInfo } from '@ui/hooks/useSingleFinanceLendInfo';
import { useTranslations } from '@ui/i18n';
import { getFerroTotalLockedValue } from '@ui/queries/ferroTotalLockedValue';
import bigNumberToNumber from '@ui/utils/bigNumberToNumber';
import { formatNumber, formatPercentage } from '@ui/utils/format';
import {
  formatGardenApy,
  gardenTokenDict,
  getGardenName,
} from '@ui/utils/garden';
import type { ReactElement, ReactNode } from 'react';
import { Children, isValidElement, useMemo } from 'react';

export const useLcroProtocols = (): TokenProtocol[] => {
  const t = useTranslations();
  const coinMode = useCoinMode();

  const tectonicTvlRet = useTectonicSupplyUsd();
  const singleFinanceFarmTvlRet = useSingleFinanceLcroFarmInfo();
  const singleFinanceLendTvlRet = useSingleFinanceLendInfo();

  // same as in useUseLcroPartnerCardsData
  const {
    ferroGardenApy: ferroApy,
    tectonicGardenApy,
    vvsCroGardenApy: vvsApy,
  } = useGardenApy(undefined, true);

  const tectonicApyRet = useTectonicApy();

  const tectonicApy = useMemo(() => {
    if (!gardenTokenDict[coinMode].TECTONIC.enabled) {
      return typeof tectonicApyRet.data !== 'undefined'
        ? tectonicApyRet.data / 100
        : undefined;
    }
    return tectonicGardenApy;
  }, [coinMode, tectonicApyRet.data, tectonicGardenApy]);

  const ferroTvlRet = useQuery({
    ...getFerroTotalLockedValue({
      coinMode,
    }),
  });

  const supportedToken: Token =
    coinMode === 'atom' ? 'latom' : coinMode === 'eth' ? 'leth' : 'lcro';

  const list: (TokenProtocol | null)[] = [
    {
      logo: '/images/use-ferro.png',
      title: 'Ferro',
      subtitle: t('Provide Liquidity'),
      titleTail: ferroApy ? `${formatGardenApy(ferroApy)}` : undefined,
      tvl: ferroTvlRet.data
        ? `$${formatNumber(ferroTvlRet.data, 2)}`
        : undefined,
      description: t(
        "Provide liquidity for {liquidCoin} on Cronos' leading stable swap and earn additional rewards on your {liquidCoin} Earn even more with the {garden}",
        {
          liquidCoin: COIN_MODE[coinMode].liquidName,
          garden: getGardenName(coinMode, 'FERRO'),
        },
      ),
      tokens: (
        <>
          <TokenIcon token="lcro" w="32px" h="32px" />
          <TokenIcon token="latom" w="32px" h="32px" />
        </>
      ),
      url: 'https://ferroprotocol.com/',
    },
    {
      logo: '/images/use-tectonic.png',
      title: 'Tectonic',
      titleTail: tectonicApy ? `${formatGardenApy(tectonicApy)}` : undefined,
      tvl: tectonicTvlRet.data
        ? `$${formatNumber(bigNumberToNumber(tectonicTvlRet.data), 2)}`
        : undefined,
      subtitle: t('Lend {coin}', {
        coin: COIN_MODE[coinMode].liquidName,
      }),
      description: t(
        'Supply {liquidCoin} on the leading money market on Cronos and earn interest from borrowers Earn additional rewards with the {garden}',
        {
          liquidCoin: COIN_MODE[coinMode].liquidName,
          garden: getGardenName(coinMode, 'TECTONIC'),
        },
      ),
      tokens: (
        <>
          <TokenIcon token="lcro" w="32px" h="32px" />
          <TokenIcon token="latom" w="32px" h="32px" />
        </>
      ),
      url: 'https://tectonic.finance/',
    },
    {
      logo: '/images/single-finance-logo.png',
      title: 'Single Finance',
      subtitle: t('Leveraged Yield Farming'),
      description: t(
        'Use {liquidCoin} for leveraged yield farming on Single Finance',
        {
          liquidCoin: COIN_MODE[coinMode].liquidName,
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://app.singlefinance.io/farm',
      titleTail: singleFinanceFarmTvlRet.data?.apy
        ? formatPercentage(singleFinanceFarmTvlRet.data.apy)
        : undefined,
      tvl: singleFinanceFarmTvlRet.data?.tvlInUSD
        ? `$${formatNumber(singleFinanceFarmTvlRet.data.tvlInUSD, 2)}`
        : undefined,
    },
    {
      logo: '/images/single-finance-logo.png',
      title: 'Single Finance',
      subtitle: t('Lend {coin}', {
        coin: COIN_MODE[coinMode].liquidName,
      }),
      description: t(
        'Supply {liquidCoin} to leveraged yield farmers and earn interest',
        {
          liquidCoin: COIN_MODE[coinMode].liquidName,
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://app.singlefinance.io/lend',
      titleTail: singleFinanceLendTvlRet.data?.apy
        ? formatPercentage(singleFinanceLendTvlRet.data.apy)
        : undefined,
      tvl: singleFinanceLendTvlRet.data?.tvlInUSD
        ? `$${formatNumber(singleFinanceLendTvlRet.data.tvlInUSD, 2)}`
        : undefined,
    },
    {
      logo: '/images/use-vvs.png',
      title: 'VVS Finance',
      titleTail: vvsApy ? `${formatGardenApy(vvsApy)}` : undefined,
      tvl: undefined,
      subtitle: 'Farm',
      description: t(
        'Put your {liquidCoin} to work on VVS Finance to earn attractive yield farming rewards!',
        {
          liquidCoin: COIN_MODE[coinMode].liquidName,
        },
      ),
      tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
      url: 'https://vvs.finance/',
    },
    isFeatureEnabled(features.wowmaxxSupport)
      ? {
          logo: '/images/wowmax.svg',
          title: 'Wowmaxx',
          subtitle: t('Trade {coin}', {
            coin: COIN_MODE[coinMode].liquidName,
          }),
          description: t(
            'Trade large amounts of {liquidCoin} with optimized slippage',
            {
              liquidCoin: COIN_MODE[coinMode].liquidName,
            },
          ),
          tokens: <TokenIcon token="lcro" w="32px" h="32px" />,
          url: 'https://app.wowmax.exchange/swap/25/LCRO/CRO',
        }
      : null,
    {
      logo: '/images/PWN_logo_black.svg',
      title: 'PWN.xyz',
      subtitle: t('Borrow against your {coin}', {
        coin: COIN_MODE[coinMode].liquidName,
      }),
      description: t('Use your {coin} to take out a peer to peer loan', {
        coin: COIN_MODE[coinMode].liquidName,
      }),
      tokens: (
        <>
          <TokenIcon token="lcro" w="32px" h="32px" />
          <TokenIcon token="vno" w="32px" h="32px" />
          <TokenIcon token="latom" w="32px" h="32px" />
        </>
      ),
      url: 'https://app.pwn.xyz/#/create-loan',
    },
  ];

  return list
    .filter((v): v is TokenProtocol => Boolean(v))
    .filter((v) => {
      const tokenTypes = getTokenTypeFromNodes(v.tokens);
      return !tokenTypes || tokenTypes.includes(supportedToken);
    });
};

const getTokenTypeFromNodes = (nodes: ReactNode): undefined | Token[] => {
  if (!isValidElement(nodes)) {
    return;
  }
  return Children.toArray(
    isTokenIconComponent(nodes) ? nodes : nodes.props.children,
  )
    .filter(isValidElement)
    .filter(isTokenIconComponent)
    .map((v) => v.props.token);
};

const isTokenIconComponent = (
  v: ReactElement,
): v is ReactElement<TokenIconProps> => {
  return v.type === TokenIcon;
};
